import { type ReactNode, useEffect, useState } from 'react'

import { useRegisterLoadingState } from '@/providers/loading/useRegisterLoadingState'
import { initGTM } from '@/utils/gtm'

import { ConfigContext } from './ConfigContext'
import type { Config } from './types'

export function ConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<Config | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Register loading state with global loading system
  useRegisterLoadingState('config', loading)

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch('/api/config')
        const data = await response.json()
        if (!data) {
          throw new Error('config not found')
        }

        setConfig({
          kinde: data.iam.kinde,
          awx: data.pay.awx,
          features: data.pay.features,
          gtm: data.pay.gtm,
        })
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unable to fetch config')
      } finally {
        setLoading(false)
      }
    }
    fetchConfig()
  }, [])

  // Initialize GTM when config is loaded
  useEffect(() => {
    if (config?.gtm?.id) {
      // Defer GTM initialization to not block render
      if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(() => initGTM(config.gtm), { timeout: 1000 })
      } else {
        setTimeout(() => initGTM(config.gtm), 0)
      }
    }
  }, [config])

  return (
    <ConfigContext.Provider value={{ config, loading, error }}>
      {error ? (
        <div className="flex items-center justify-center min-h-screen bg-white dark:bg-gray-900 text-red-600 dark:text-red-400">
          Error loading config: {error}
        </div>
      ) : (
        children
      )}
    </ConfigContext.Provider>
  )
}
